import { LayerTypeLayerSummaryProps } from "../../../types";
import { useKeyIndicatorTags } from "react-migration/lib/map/useKeyIndicatorTags";
import { TagColor } from "react-migration/components/Tag";
import { buildCompsQuery } from "react-migration/domains/comparables/util/BuildCompsQuery";
import { IComparablesTransaction, ISortQuery } from "react-migration/lib/typings/comparables/Comparables";
import { defaultHometrackFilters } from "react-migration/domains/comparables/util/defaultFilters";
import { disabledZooplaFields } from "react-migration/domains/comparables/util/DisabledFilters";
import { formatToCurrency } from "react-migration/lib/util/numberFormat";
import { useHometrackValue } from "./useHometrackValue";
import { useSelectionSidebarLoading } from "../../../Bundle/useSelectionSidebarLoadingState";
import { useRadiusInput } from "react-migration/lib/map/useRadiusInput";
import { useMemo, useState } from "react";
import { convertGeoJsonToPolygonGeoFilter } from "react-migration/domains/comparables/util/Geo";
import { CurrencyCode } from "react-migration/lib/typings/Currency";
import { HometrackValuesRental } from "./HometrackValuesRental";
import { HometrackLayerFilters, HometrackLayerFilterState } from "./HometrackLayerFilters";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";

export const HometrackLayerSummaryRental = ({
  expanded,
  print,
  selection,
  onLoadingChange,
}: LayerTypeLayerSummaryProps) => {
  const { RadiusInput, bufferedGeometry } = useRadiusInput({
    geometry: selection?.feature?.geometry || null,
    enabled: !!expanded,
    defaultRadius: 300,
    onRadiusChange: (radius) => {
      logEventV2({
        name: WorkbenchEventName.WORKBENCH_FILTER_CLICK,
        properties: { cardName: "comparables", filterName: "radius", filterState: radius },
      });
    },
  });

  const searchPoly = useMemo(
    () => ({
      poly_geo_filter: bufferedGeometry?.geometry ? convertGeoJsonToPolygonGeoFilter(bufferedGeometry.geometry) : null,
    }),
    [bufferedGeometry]
  );
  const [filters, setFilters] = useState<HometrackLayerFilterState<"rent">>({
    displayBy: "IndexedCurrentValue",
    bedrooms: 2,
  });

  const searchCriteria = {
    ...buildCompsQuery(defaultHometrackFilters, disabledZooplaFields),
    ...searchPoly,
    bedrooms: filters.bedrooms ? { from: filters.bedrooms, to: filters.bedrooms } : undefined,
  };

  const sortCriteria: ISortQuery<IComparablesTransaction> = {
    fieldName: "date_of_transfer",
    sortType: {
      order: "desc",
    },
  };

  const { value: lastValue, loading: lastValueLoading } = useHometrackValue(
    searchCriteria,
    sortCriteria,
    filters.displayBy === "IndexedCurrentValue" ? "RENT_ESTIMATE" : "RENT_ASKING"
  );

  useKeyIndicatorTags(() => {
    if (!lastValue) return [];

    return [
      {
        tag: formatToCurrency({
          amount: lastValue,
          currency: CurrencyCode.GBP,
          maximumFractionDigits: 0,
        }),
        color: TagColor.DEFAULT,
      },
    ];
  }, [lastValue]);

  useSelectionSidebarLoading(onLoadingChange, lastValueLoading);

  return (
    <div className="flex flex-col gap-y-4">
      <HometrackLayerFilters type="rent" filters={filters} onFilterChange={setFilters} />
      <div className="flex items-center justify-between">{RadiusInput}</div>
      {(print || expanded) && (
        <HometrackValuesRental
          searchCriteria={searchCriteria}
          sortCriteria={sortCriteria}
          displayBy={filters.displayBy}
        />
      )}
    </div>
  );
};

HometrackLayerSummaryRental.icon = "icon-lt-hometrack";
